
import { useState } from "react";
import { TextField } from "@mui/material";
import "../../../assets/css/CustomTextInput.css";

interface CustomTextInputProps {
  label: string;
  value: string | number;
  onChange: (value: string | number) => void;
  type?: "text" | "number" | "date";
  formatNumber?: boolean;
  fullWidth?: boolean;
  margin?: "none" | "dense" | "normal";
  heightOverride?: string;
  redOutline?: boolean;
  multiline?: boolean;
  minRows?: number;
  onBlur?: () => void;
  disabled?: boolean;
  required?: boolean;
}

/**
 * Formats a raw numeric string with en-GB thousands separators for display.
 * Works on the string itself (rather than via Number/toLocaleString) so that
 * decimal places are never rounded away, and returns the input unchanged when
 * it isn't a plain decimal number so that NaN is never displayed.
 */
const formatNumberForDisplay = (value: string | number): string => {
  const raw = String(value).trim();
  const match = /^(-?)(\d*)(?:\.(\d*))?$/.exec(raw);
  if (!match || !/\d/.test(raw)) return raw;
  const [, sign, intPart, fraction = ""] = match;
  const integer = (intPart.replace(/^0+(?=\d)/, "") || "0").replace(
    /\B(?=(\d{3})+(?!\d))/g,
    ","
  );
  return `${sign}${integer}${fraction ? `.${fraction}` : ""}`;
};

const CustomTextInput: React.FC<CustomTextInputProps> = ({
  label,
  value,
  onChange,
  type = "text",
  formatNumber = false,
  fullWidth = true,
  margin = "normal",
  heightOverride,
  redOutline = false,
  multiline = false,
  minRows = 5,
  onBlur = () => {},
  disabled = false,
  required = false,
}) => {
  const isNumber = type === "number";
  const isDate = type === "date";
  const [isFocused, setIsFocused] = useState(false);

  // While focused, show the raw value so partial input such as "1234." or "-"
  // survives; format with thousands separators only when not editing.
  const displayValue =
    isNumber && formatNumber && !isFocused && value !== "" && value !== null && value !== undefined
      ? formatNumberForDisplay(value)
      : value;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let raw = e.target.value;
    if (isNumber) {
      raw = raw.replace(/[^0-9.-]/g, "");
      const parts = raw.split('.');
      if (parts.length > 2) {
        raw = parts[0] + '.' + parts.slice(1).join('');
      }
      if (raw.includes('-') && raw.indexOf('-') !== 0) {
        raw = raw.replace(/-/g, '');
      }
      raw = raw.replace(/,/g, "");
      onChange(raw === "" ? "" : raw);
    } else {
      onChange(raw);
    }
  };

  return (
    <TextField
      className="custom-text-input"
      label={label}
      fullWidth={fullWidth}
      multiline={multiline}
      minRows={multiline ? minRows : undefined}
      margin={margin}
      type={isDate ? "date" : "text"}
      value={displayValue}
      onChange={handleChange}
      onFocus={() => setIsFocused(true)}
      onBlur={() => {
        setIsFocused(false);
        onBlur();
      }}
      disabled={disabled}
      required={required}
      sx={{
        '& .MuiOutlinedInput-root': {
          height: multiline ? 'auto' : (heightOverride || 32),
          fontFamily: 'Helvetica Neue, sans-serif !important',
          fontSize: '12px',
          padding: '6px',
          '& input': {
            padding: '6px 8px',
            height: '100%',
            boxSizing: 'border-box',
          },
          '& fieldset': {
            borderColor: redOutline ? '#BA2C2C' : undefined,
          },
          '&.Mui-focused fieldset': {
            border: redOutline ? '1px solid #BA2C2C' : '1px solid #245EA0',
          },
        },
        '& .MuiInputLabel-root': {
          fontSize: '0.75rem',
          color: redOutline ? '#BA2C2C' : '#8E8E93',
          '&.Mui-focused': {
            color: redOutline ? '#BA2C2C' : '#245EA0',
          },
        },
      }}
      InputLabelProps={{ shrink: true }}
    />
  );
};

export { CustomTextInput };
